/**
 * AP-ACCEPT-01B: Supplier Delivery Detail + Resubmit
 * Route: /portal/deliveries/:submissionId (protected)
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, AlertCircle, CheckCircle2, Clock, RotateCcw, FileText, Plus, Trash2 } from "lucide-react";
import { usePortalUser } from "@/components/PortalProtectedRoute";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  PENDING_REVIEW:       { label: "Pending Review",        color: "#92400E", bg: "#FEF3C7" },
  UNDER_REVIEW:         { label: "Under Review",          color: "#1E40AF", bg: "#EFF6FF" },
  ACCEPTED:             { label: "Accepted",              color: "#166534", bg: "#F0FDF4" },
  PARTIALLY_ACCEPTED:   { label: "Partially Accepted",    color: "#166534", bg: "#F0FDF4" },
  REJECTED:             { label: "Rejected",              color: "#991B1B", bg: "#FEF2F2" },
  CORRECTION_REQUESTED: { label: "Correction Requested",  color: "#9A3412", bg: "#FFF7ED" },
  RESUBMITTED:          { label: "Resubmitted",           color: "#5B21B6", bg: "#F5F3FF" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#666", bg: "#F5F5F5" };
  return (
    <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export default function SupplierDeliveryDetail() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { portalUser } = usePortalUser();

  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showResubmitForm, setShowResubmitForm] = useState(false);

  // Resubmit state
  const [resubNotes, setResubNotes] = useState("");
  const [resubLines, setResubLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [resubError, setResubError] = useState("");

  useEffect(() => { loadSubmission(); }, [submissionId]);

  async function loadSubmission() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/portal/deliveries/${submissionId}`, { withCredentials: true });
      setSub(res.data);
      if (res.data.status === "CORRECTION_REQUESTED") {
        setResubLines((res.data.line_submissions || []).map((l) => ({
          po_line_id: l.po_line_id,
          description: l.po_line_description,
          delivered_quantity: l.delivered_quantity,
          delivery_reference: l.delivery_reference,
          delivery_date: l.delivery_date,
          milestone: l.milestone || "",
          notes: l.notes || "",
          evidence_documents: l.evidence_documents || [],
        })));
      }
    } catch (err) {
      setError(err.response?.status === 403 ? "You do not have access to this submission" : "Failed to load submission");
    } finally {
      setLoading(false);
    }
  }

  async function handleResubmit(e) {
    e.preventDefault();
    setResubError("");
    for (const l of resubLines) {
      if (!l.delivered_quantity || parseFloat(l.delivered_quantity) <= 0) { setResubError(`Quantity required for ${l.description.slice(0, 40)}`); return; }
      if (!l.delivery_reference.trim()) { setResubError(`Delivery reference required for ${l.description.slice(0, 40)}`); return; }
    }
    setSubmitting(true);
    try {
      const res = await axios.put(`${API}/api/portal/deliveries/${submissionId}/resubmit`, {
        submission_notes: resubNotes.trim() || undefined,
        line_submissions: resubLines.map((l) => ({
          po_line_id: l.po_line_id,
          delivered_quantity: String(parseFloat(l.delivered_quantity)),
          delivery_reference: l.delivery_reference.trim(),
          delivery_date: l.delivery_date,
          milestone: l.milestone || undefined,
          notes: l.notes.trim() || undefined,
          evidence_documents: l.evidence_documents.filter((d) => d.document_name?.trim()),
        })),
      }, { withCredentials: true });
      toast.success("Resubmission sent successfully");
      navigate(`/portal/deliveries/${res.data.id}`);
    } catch (err) {
      setResubError(err.response?.data?.detail || "Resubmission failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <LoadingScreen />;
  if (error || !sub) return (
    <div style={pageStyle}>
      <div style={{ padding: "80px 24px", textAlign: "center" }}>
        <AlertCircle size={36} style={{ color: "#D97706", margin: "0 auto 12px" }} />
        <p style={{ color: "#1A1A1A", fontSize: "14px" }}>{error || "Submission not found"}</p>
        <button onClick={() => navigate("/portal/deliveries")} style={backBtn}>Back to My Deliveries</button>
      </div>
    </div>
  );

  return (
    <div data-testid="delivery-detail-page" style={pageStyle}>
      <nav style={navStyle}>
        <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "18px", fontWeight: 600, color: "#C9A15F" }}>Prashasta</span>
        <span style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.15)", margin: "0 16px" }} />
        <span style={{ fontSize: "12px", color: "#8A7A6A" }}>Partner Portal</span>
      </nav>

      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "32px 24px" }}>
        <button onClick={() => navigate("/portal/deliveries")} style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#8A7A6A", background: "none", border: "none", cursor: "pointer", padding: "0 0 20px" }}>
          <ArrowLeft size={14} /> Back to My Deliveries
        </button>

        {/* Header */}
        <div style={{ background: "#FFFFFF", border: "1px solid #EDE5D8", borderRadius: "12px", padding: "24px 28px", marginBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
            <div>
              <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "24px", fontWeight: 600, color: "#1A1A1A", margin: "0 0 4px" }}>
                {sub.po_number || "Purchase Order"}
              </h1>
              <p style={{ fontSize: "12px", color: "#9A8A78", margin: 0 }}>Submitted {fmtDate(sub.submitted_at)}{sub.resubmission_count > 0 ? ` · Resubmission #${sub.resubmission_count}` : ""}</p>
            </div>
            <StatusBadge status={sub.status} />
          </div>
          {sub.review_notes && (
            <div style={{ padding: "10px 14px", background: sub.status === "CORRECTION_REQUESTED" ? "#FFF7ED" : "#F0FDF4", border: `1px solid ${sub.status === "CORRECTION_REQUESTED" ? "#FED7AA" : "#BBF7D0"}`, borderRadius: "8px", fontSize: "13px", color: "#1A1A1A" }}>
              <strong style={{ fontSize: "11px", color: "#9A8A78", textTransform: "uppercase", letterSpacing: "0.5px" }}>Reviewer Note:</strong>
              <p style={{ margin: "4px 0 0" }}>{sub.review_notes}</p>
            </div>
          )}
        </div>

        {/* Line submissions */}
        <div style={{ background: "#FFFFFF", border: "1px solid #EDE5D8", borderRadius: "12px", padding: "20px 24px", marginBottom: "16px" }}>
          <p style={{ fontSize: "11px", fontWeight: 600, color: "#9A8A78", letterSpacing: "0.5px", textTransform: "uppercase", margin: "0 0 16px" }}>
            Submitted Lines ({sub.line_submissions?.length})
          </p>
          {(sub.line_submissions || []).map((l, idx) => (
            <div key={idx} data-testid={`submitted-line-${idx}`} style={{ padding: "14px", background: "#FAF6F0", borderRadius: "8px", marginBottom: "10px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
                <p style={{ fontSize: "13px", fontWeight: 600, color: "#1A1A1A", margin: 0 }}>{l.po_line_description}</p>
                <LineStatusBadge status={l.line_review_status} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "12px", fontSize: "12px", color: "#6B5B4A" }}>
                <div><span style={metaLabel}>Delivered</span><strong>{l.delivered_quantity} {l.po_line_unit}</strong></div>
                <div><span style={metaLabel}>Reference</span><strong>{l.delivery_reference}</strong></div>
                <div><span style={metaLabel}>Date</span><strong>{fmtDate(l.delivery_date)}</strong></div>
                {l.accepted_quantity != null && (
                  <>
                    <div><span style={metaLabel}>Accepted</span><strong style={{ color: "#2E7D32" }}>{l.accepted_quantity}</strong></div>
                    <div><span style={metaLabel}>Rejected</span><strong style={{ color: "#C62828" }}>{l.rejected_quantity}</strong></div>
                  </>
                )}
              </div>
              {l.evidence_documents?.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                  <span style={{ fontSize: "11px", color: "#9A8A78" }}>Evidence: </span>
                  {l.evidence_documents.map((d, i) => (
                    <span key={i} style={{ fontSize: "11px", background: "#EDE5D8", padding: "2px 6px", borderRadius: "4px", marginRight: "4px", color: "#6B5B4A" }}>{d.document_name} ({d.document_type})</span>
                  ))}
                </div>
              )}
              {l.line_review_notes && <p style={{ fontSize: "12px", color: "#8A7A6A", marginTop: "6px", fontStyle: "italic" }}>{l.line_review_notes}</p>}
            </div>
          ))}
        </div>

        {/* Resubmit form */}
        {sub.status === "CORRECTION_REQUESTED" && (
          <div style={{ background: "#FFFFFF", border: "1px solid #FED7AA", borderRadius: "12px", padding: "20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <div>
                <p style={{ fontSize: "13px", fontWeight: 600, color: "#9A3412", margin: 0 }}>Correction Required</p>
                <p style={{ fontSize: "12px", color: "#6B5B4A", margin: "2px 0 0" }}>Please address the reviewer's feedback and resubmit your delivery evidence</p>
              </div>
              <button data-testid="toggle-resubmit-btn" onClick={() => setShowResubmitForm(!showResubmitForm)} style={{ padding: "8px 16px", background: "#1A1A1A", color: "#F5EFE6", border: "none", borderRadius: "8px", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
                <RotateCcw size={13} /> {showResubmitForm ? "Close" : "Resubmit Evidence"}
              </button>
            </div>
            {showResubmitForm && (
              <form data-testid="resubmit-form" onSubmit={handleResubmit}>
                <textarea value={resubNotes} onChange={(e) => setResubNotes(e.target.value)} placeholder="Describe what you have corrected…" rows={2} style={{ ...inputStyle, marginBottom: "14px" }} />
                {resubLines.map((l, idx) => (
                  <div key={idx} style={{ padding: "12px", background: "#FAF6F0", borderRadius: "8px", marginBottom: "10px" }}>
                    <p style={{ fontSize: "12px", fontWeight: 600, color: "#1A1A1A", margin: "0 0 10px" }}>{l.description}</p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
                      <div>
                        <label style={smallLabelStyle}>Qty *</label>
                        <input type="number" min="0.001" step="0.001" value={l.delivered_quantity} onChange={(e) => setResubLines((prev) => prev.map((x, i) => i === idx ? { ...x, delivered_quantity: e.target.value } : x))} style={inputStyle} required />
                      </div>
                      <div>
                        <label style={smallLabelStyle}>Reference *</label>
                        <input type="text" value={l.delivery_reference} onChange={(e) => setResubLines((prev) => prev.map((x, i) => i === idx ? { ...x, delivery_reference: e.target.value } : x))} style={inputStyle} required />
                      </div>
                      <div>
                        <label style={smallLabelStyle}>Date *</label>
                        <input type="date" value={l.delivery_date} onChange={(e) => setResubLines((prev) => prev.map((x, i) => i === idx ? { ...x, delivery_date: e.target.value } : x))} style={inputStyle} required />
                      </div>
                    </div>
                  </div>
                ))}
                {resubError && <p style={{ fontSize: "13px", color: "#DC2626", marginBottom: "12px" }}>{resubError}</p>}
                <button type="submit" data-testid="confirm-resubmit-btn" disabled={submitting} style={{ padding: "10px 24px", background: "#C9A15F", color: "#111", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer" }}>
                  {submitting ? "Submitting…" : "Confirm Resubmission"}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LineStatusBadge({ status }) {
  const map = { PENDING: { bg: "#F5F5F5", text: "#616161" }, ACCEPTED: { bg: "#F0FDF4", text: "#166534" }, PARTIALLY_ACCEPTED: { bg: "#F0FDF4", text: "#166534" }, REJECTED: { bg: "#FEF2F2", text: "#991B1B" } };
  const cfg = map[status] || { bg: "#F5F5F5", text: "#666" };
  return <span style={{ fontSize: "10px", fontWeight: 600, padding: "2px 7px", borderRadius: "8px", background: cfg.bg, color: cfg.text }}>{status}</span>;
}

function LoadingScreen() {
  return (
    <div style={pageStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh" }}>
        <div style={{ width: "28px", height: "28px", border: "2px solid #EDE5D8", borderTopColor: "#C9A15F", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );
}

const pageStyle = { minHeight: "100vh", background: "#FBF7F2", fontFamily: "'DM Sans', sans-serif" };
const navStyle = { background: "#1A1A1A", borderBottom: "1px solid rgba(201,161,95,0.2)", padding: "0 24px", height: "60px", display: "flex", alignItems: "center", position: "sticky", top: 0, zIndex: 10 };
const backBtn = { padding: "8px 16px", background: "#1A1A1A", color: "#F5EFE6", border: "none", borderRadius: "8px", fontSize: "12px", cursor: "pointer", marginTop: "12px" };
const metaLabel = { display: "block", fontSize: "10px", color: "#9A8A78", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" };
const inputStyle = { width: "100%", padding: "9px 12px", border: "1px solid #D4C5B0", borderRadius: "8px", fontSize: "13px", color: "#1A1A1A", background: "#FAFAF8", outline: "none", boxSizing: "border-box", fontFamily: "'DM Sans', sans-serif" };
const smallLabelStyle = { display: "block", fontSize: "10px", color: "#9A8A78", marginBottom: "4px", letterSpacing: "0.5px", textTransform: "uppercase" };
